import { Draft, produce } from 'immer';

import {
  TEMPLATE__FIELDS_OVER_MAX_DOCUMENT_PAGE_DELETE,
  TEMPLATE_CLEAR_ALL_STATE,
  TEMPLATE_FIELD__ALLOW_SIGNATURE_TYPES_SELECT,
  TEMPLATE_FIELD_CONTEXT_MENU_CHANGE,
  TEMPLATE_FIELD_CREATE,
  TEMPLATE_INFORMATION_FETCH_REQUEST,
  TEMPLATE_INFORMATION_FETCH_SUCCESS,
  TEMPLATE_PARTICIPANT_DELETE,
} from '@app/store/actionTypes';
import CheckboxGroupInputMode from '@app/store/CheckboxGroupInputMode';
import FieldType from '@app/store/FieldType';
import FontType from '@app/store/FontType';
import {
  scheduledDocumentPreviewDataFetched,
  setupTemplateByDraft,
  TemplateAction,
} from '@app/store/template/actions';
import FieldInformation from '@app/store/template/FieldInformation';
import PropType from '@app/store/template/PropType';
import TextAlign from '@app/store/TextAlign';

export interface TemplateFieldInformationState {
  [id: string]: FieldInformation;
}

const initialState: TemplateFieldInformationState = {};

const reducer = produce(
  (
    draft: Draft<TemplateFieldInformationState>,
    action: TemplateAction
  ): TemplateFieldInformationState => {
    switch (action.type) {
      case TEMPLATE_CLEAR_ALL_STATE:
      case TEMPLATE_INFORMATION_FETCH_REQUEST:
        return initialState;
      case scheduledDocumentPreviewDataFetched.type:
      case TEMPLATE_INFORMATION_FETCH_SUCCESS: {
        const { participants } = action.payload;
        participants.forEach((participant) => {
          const { id: participantId, fields } = participant;
          const { signatureFields, textFields, checkboxGroups } = fields;

          checkboxGroups.forEach((checkboxGroup) => {
            draft[checkboxGroup.id] = {
              id: checkboxGroup.id,
              dataLabel: checkboxGroup.groupLabel,
              participantId,
              required: checkboxGroup.required,
              checkboxCount: checkboxGroup.checkboxNumber,
              checkboxGroupMode: checkboxGroup.checkboxGroupMode,
              type: FieldType.CHECKBOX_GROUP,
            };
            checkboxGroup.children.forEach((checkboxField) => {
              draft[checkboxField.id] = {
                dataLabel: checkboxField.dataLabel,
                index: checkboxField.index,
                id: checkboxField.id,
                participantId,
                type: checkboxField.fieldType,
              };
            });
          });

          signatureFields.forEach((signatureField) => {
            const { allowSignatureTypes, id: fieldId } = signatureField;

            draft[fieldId] = {
              allowSignatureTypes,
              dataLabel: signatureField.dataLabel,
              id: fieldId,
              participantId,
              type: signatureField.fieldType,
            };
          });
          textFields.forEach((textField) => {
            const { id: fieldId, textAlign } = textField;
            draft[fieldId] = {
              dataLabel: textField.dataLabel,
              fontFamily: textField.fontType,
              fontSize: textField.fontSize,
              id: fieldId,
              participantId,
              required: textField.isRequired,
              textAlign,
              type: textField.fieldType,
            };
          });
        });
        return draft;
      }

      case setupTemplateByDraft.type: {
        action.payload.participants.forEach((participant) => {
          participant.fields.forEach((field) => {
            const participantId = participant.id;
            const { dataLabel, id } = field;

            switch (field.type) {
              case FieldType.CHECKBOX_GROUP:
                draft[field.id] = {
                  id: field.id,
                  dataLabel: field.dataLabel,
                  participantId,
                  required: field.required,
                  checkboxCount: field.checkboxNumber,
                  checkboxGroupMode: field.checkboxGroupMode,
                  type: FieldType.CHECKBOX_GROUP,
                };
                field.children.forEach((checkboxField) => {
                  draft[checkboxField.id] = {
                    dataLabel: checkboxField.dataLabel,
                    index: checkboxField.index,
                    id: checkboxField.id,
                    participantId,
                    type: FieldType.CHECKBOX_FIELD,
                  };
                });
                break;
              case FieldType.TEXT_FIELD:
                draft[field.id] = {
                  dataLabel,
                  id,
                  participantId,
                  textAlign: field.textStyle.align,
                  fontFamily: field.textStyle.font,
                  fontSize: field.textStyle.size,
                  required: field.required,
                  type: FieldType.TEXT_FIELD,
                };
                break;
              case FieldType.SIGNATURE_FIELD:
                draft[field.id] = {
                  dataLabel,
                  id,
                  participantId,
                  allowSignatureTypes: field.signatureTypes,
                  type: FieldType.SIGNATURE_FIELD,
                };
                break;
              default:
                break;
            }
          });
        });

        return draft;
      }
      case TEMPLATE_FIELD_CREATE: {
        const {
          meta: { id },
          information,
        } = action.payload;
        draft[id] = information;

        /**
         * 필드 생성할 때 텍스트 정렬 기본값은 왼쪽(LEFT)입니다.
         */
        const target = draft[id];
        if (target.type === FieldType.TEXT_FIELD) {
          target.textAlign = TextAlign.LEFT;
        }
        return draft;
      }
      case TEMPLATE_FIELD_CONTEXT_MENU_CHANGE: {
        const { id, propType, value } = action.payload;
        const targetField = draft[id];

        if (propType === PropType.DATA_LABEL) {
          targetField.dataLabel = value as string;
        }

        if (propType === PropType.CHECKBOX_COUNT && targetField.type === FieldType.CHECKBOX_GROUP) {
          targetField.checkboxCount = Number(value);
        }

        if (
          propType === PropType.CHECKBOX_GROUP_MODE &&
          targetField.type === FieldType.CHECKBOX_GROUP
        ) {
          targetField.checkboxGroupMode = value as CheckboxGroupInputMode;
        }

        if (propType === PropType.TEXT_ALIGN && targetField.type === FieldType.TEXT_FIELD) {
          targetField.textAlign = value as TextAlign;
        }

        if (propType === PropType.TEXT_FONT_FAMILY && targetField.type === FieldType.TEXT_FIELD) {
          targetField.fontFamily = value as FontType;
        }

        if (propType === PropType.TEXT_FONT_SIZE && targetField.type === FieldType.TEXT_FIELD) {
          targetField.fontSize = Number(value);
        }

        if (propType === PropType.REQUIRED && targetField.type === FieldType.TEXT_FIELD) {
          targetField.required = Boolean(value);
        }

        if (propType === PropType.REQUIRED && targetField.type === FieldType.CHECKBOX_GROUP) {
          targetField.required = Boolean(value);
        }
        return draft;
      }
      case TEMPLATE__FIELDS_OVER_MAX_DOCUMENT_PAGE_DELETE: {
        const { fieldIds } = action.payload;
        fieldIds.forEach((fieldId) => {
          if (fieldId in draft) {
            delete draft[fieldId];
          }
        });
        return draft;
      }
      case TEMPLATE_PARTICIPANT_DELETE: {
        const { fieldIds } = action.payload;
        fieldIds.forEach((fieldId) => {
          const { type } = draft[fieldId];
          if (type === FieldType.SIGNATURE_FIELD) {
            delete draft[fieldId];
          }
        });
        return draft;
      }
      case TEMPLATE_FIELD__ALLOW_SIGNATURE_TYPES_SELECT: {
        const { allowSignatureTypes, participantId } = action.payload;

        Object.values(draft).forEach((value) => {
          if (value.type === FieldType.SIGNATURE_FIELD && value.participantId === participantId) {
            value.allowSignatureTypes = allowSignatureTypes;
          }
        });

        return draft;
      }
      default:
        return draft;
    }
  },
  initialState
);

export default reducer;
